import { createSelector } from '@reduxjs/toolkit';
import { getTimezone } from 'reducers/utils';
import { selectors as dataGridTableSelectors } from '../Modules/dataGridTable';
import { selectors as followupEventSelectors } from '../Modules/followupEvents';
import { selectors as filtersSelectors } from '../Modules/filters';
import { transform } from 'utils/DataTransform/Events/eventReportList';
import {
  transformedSectionedEvents,
  transform as eventDetailsTransform,
} from 'utils/DataTransform/Events/eventDetails';
import { PAGE_URLS, ROLES } from 'utils/constants';
import { getUserRoles } from 'reducers/users';
import {
  checkQualification,
  checkUserRole,
  checkUserRoleAndEventStatus,
} from '../operational/helper';
import { getSecurityCompanyId } from 'reducers/securityCompany';
import {
  eventStatusValue as eventStatus,
  BASE_EVENT_STATUS,
} from 'utils/constants/eventStatus';

export const getEventListTransformed = createSelector(
  dataGridTableSelectors.getGridTableList,
  getTimezone,
  (eventList, timezone) => {
    if (!eventList) {
      return [];
    }
    const list = transform(eventList, timezone);
    return list;
  },
);
export const getEventListRaw = createSelector(
  dataGridTableSelectors.getGridTableList,
  eventList => {
    if (!eventList) {
      return [];
    }
    return eventList.map(event => ({
      ...event,
      pageName: PAGE_URLS.EVENTS.link,
    }));
  },
);

export const getTotalRowsCount = createSelector(
  dataGridTableSelectors.getTotalGridRows,
  rowsCount => rowsCount,
);

export const getEventFilters = createSelector(
  filtersSelectors.getFilterValues,
  filterValues => {
    return filterValues;
  },
);
export const getEventFilterKey = createSelector(
  filtersSelectors.getFilterKey,
  key => {
    return key;
  },
);

export const getEventDetailsTransformedSectioned = createSelector(
  dataGridTableSelectors.getGridRowDetails,
  details => {
    if (!details) {
      return {};
    }
    const transformedDetails = transformedSectionedEvents(details);
    return transformedDetails;
  },
);

export const getEventDetailsRaw = createSelector(
  dataGridTableSelectors.getGridRowDetails,
  details => {
    if (!details) {
      return {};
    }
    return details;
  },
);
export const getEventDetailsForForms = createSelector(
  getEventDetailsRaw,
  details => {
    if (details && Object.keys(details).length === 0) {
      return {};
    }
    return eventDetailsTransform(details);
  },
);
export const getEventDetailsForOperationalComponent = createSelector(
  dataGridTableSelectors.getGridRowDetails,
  details => {
    if (details && Object.keys(details).length === 0) {
      return {};
    }
    return eventDetailsTransform(details);
  },
);

export const checkIfQualifiedToSubmitReview = createSelector(
  getEventDetailsForOperationalComponent,
  getUserRoles,
  (details, userRoles) => {
    if (details && Object.keys(details).length === 0) {
      return false;
    }
    const checkRoleAndStatus = checkUserRoleAndEventStatus({
      userRoles,
      qualifiedUserRoles: [ROLES.SUPERVISOR.id],
      event: details,
      qualifiedEventStatus: [eventStatus.DRAFT, eventStatus.UPDATED],
    });
    const matchDistribution =
      details?.generalInfo.distribution.value !==
      BASE_EVENT_STATUS.INTERNAL.value;

    return checkRoleAndStatus && matchDistribution;
  },
);

export const checkQualifiedToDistributeAndAssign = createSelector(
  getEventDetailsForOperationalComponent,
  getUserRoles,
  getSecurityCompanyId,
  (details, userRoles, securityCompanyId) => {
    if (details && Object.keys(details).length === 0) {
      return false;
    }
    const checkRole = checkUserRole(userRoles, [
      ROLES.MANAGER.id,
      ROLES.SECURITY_COMPANY_MANAGER.id,
      ROLES.SUPERVISOR.id,
    ]);
    const ifSecurityCompanyMatch =
      securityCompanyId === details?.others.securityCompanyId.value;
    return checkRole && ifSecurityCompanyMatch;
  },
);

export const checkIfQualifiedToPublish = createSelector(
  getEventDetailsForOperationalComponent,
  getUserRoles,
  (event, userRoles) => {
    if (event && Object.keys(event).length === 0) {
      return false;
    }
    const checkIfQualified = checkQualification({
      userRoles,
      qualifiedUserRoles: [
        ROLES.SUPERVISOR.id,
        ROLES.MANAGER.id,
        ROLES.SECURITY_COMPANY_MANAGER.id,
      ],
      event,
      qualifiedEventStatus: [eventStatus.DRAFT, eventStatus.UPDATED],
      eventDist: event?.generalInfo.distribution.value,
    });
    return checkIfQualified;
  },
);

export const checkIfqualifiedToReview = createSelector(
  getEventDetailsForOperationalComponent,
  getUserRoles,
  (event, userRoles) => {
    if (event && Object.keys(event).length === 0) {
      return false;
    }
    const checkIfQualifiedToReview = checkQualification({
      userRoles,
      qualifiedUserRoles: [
        ROLES.SUPERVISOR.id,
        ROLES.MANAGER.id,
        ROLES.SECURITY_COMPANY_MANAGER.id,
      ],
      event,
      qualifiedEventStatus: [eventStatus.READY_FOR_APPROVAL],
      eventDist: event?.generalInfo.distribution.value,
    });
    return checkIfQualifiedToReview;
  },
);

export const getForceRefresh = createSelector(
  dataGridTableSelectors.getForceRefresh,
  forceRefresh => forceRefresh,
);

export const getBaseEventParentID = createSelector(
  getEventDetailsRaw,
  details => {
    if (Object.keys(details).length === 0) {
      return '';
    }
    return details.parentEventID;
  },
);

/// -------Followup Event Selectors----------------------
export const getFollowupEventID = createSelector(
  followupEventSelectors.getFollowEventID,
  followupEventID => followupEventID,
);
export const getFollowupEventDetails = createSelector(
  followupEventSelectors.getFollowupEventDetails,
  followupEventDetails => followupEventDetails,
);
export const getFollowupEventDetailsTransformedSectioned = createSelector(
  getFollowupEventDetails,
  details => {
    if (Object.keys(details).length === 0) {
      return {};
    }
    const transformedDetails = transformedSectionedEvents(details);
    return transformedDetails;
  },
);

export const getFollowupEventDetailsForForms = createSelector(
  getFollowupEventDetails,
  details => {
    if (Object.keys(details).length === 0) {
      return {};
    }
    return eventDetailsTransform(details);
  },
);
export const getFollowupEventParentID = createSelector(
  getFollowupEventDetails,
  details => {
    if (Object.keys(details).length === 0) {
      return {};
    }
    return details.parentEventID;
  },
);

export const getEventDetailsForFollowupOperationalComponent = createSelector(
  followupEventSelectors.getFollowupEventDetails,
  details => {
    if (details && Object.keys(details).length === 0) {
      return {};
    }
    return eventDetailsTransform(details);
  },
);

export const checkIfQualifiedToSubmitReviewFollowup = createSelector(
  getEventDetailsForFollowupOperationalComponent,
  getUserRoles,
  (details, userRoles) => {
    if (details && Object.keys(details).length === 0) {
      return false;
    }
    const checkRoleAndStatus = checkUserRoleAndEventStatus({
      userRoles,
      qualifiedUserRoles: [ROLES.SUPERVISOR.id],
      event: details,
      qualifiedEventStatus: [eventStatus.DRAFT, eventStatus.UPDATED],
    });
    const matchDistribution =
      details?.generalInfo.distribution.value !==
      BASE_EVENT_STATUS.INTERNAL.value;

    return checkRoleAndStatus && matchDistribution;
  },
);

export const checkQualifiedToDistributeAndAssignFollowup = createSelector(
  getEventDetailsForFollowupOperationalComponent,
  getUserRoles,
  getSecurityCompanyId,
  (details, userRoles, securityCompanyId) => {
    if (details && Object.keys(details).length === 0) {
      return false;
    }
    const checkRole = checkUserRole(userRoles, [
      ROLES.MANAGER.id,
      ROLES.SECURITY_COMPANY_MANAGER.id,
      ROLES.SUPERVISOR.id,
    ]);
    const ifSecurityCompanyMatch =
      securityCompanyId === details?.others.securityCompanyId.value;
    return checkRole && ifSecurityCompanyMatch;
  },
);

export const checkIfQualifiedToPublishFollowup = createSelector(
  getEventDetailsForFollowupOperationalComponent,
  getUserRoles,
  (event, userRoles) => {
    if (event && Object.keys(event).length === 0) {
      return false;
    }
    const checkIfQualified = checkQualification({
      userRoles,
      qualifiedUserRoles: [
        ROLES.SUPERVISOR.id,
        ROLES.MANAGER.id,
        ROLES.SECURITY_COMPANY_MANAGER.id,
      ],
      event,
      qualifiedEventStatus: [eventStatus.DRAFT, eventStatus.UPDATED],
      eventDist: event?.generalInfo.distribution.value,
    });
    return checkIfQualified;
  },
);

export const checkIfqualifiedToReviewFollowup = createSelector(
  getEventDetailsForFollowupOperationalComponent,
  getUserRoles,
  (event, userRoles) => {
    if (event && Object.keys(event).length === 0) {
      return false;
    }
    const checkIfQualifiedToReview = checkQualification({
      userRoles,
      qualifiedUserRoles: [
        ROLES.SUPERVISOR.id,
        ROLES.MANAGER.id,
        ROLES.SECURITY_COMPANY_MANAGER.id,
      ],
      event,
      qualifiedEventStatus: [eventStatus.READY_FOR_APPROVAL],
      eventDist: event?.generalInfo.distribution.value,
    });
    return checkIfQualifiedToReview;
  },
);

export const getSelectedRowID = createSelector(
  dataGridTableSelectors.getSelectedRowID,
  selectedRowID => selectedRowID,
);
